import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PortfolioResult } from "@/lib/portfolio";

interface PortfolioTableProps {
  portfolio: PortfolioResult;
  orgSlug: string;
}

export function PortfolioTable({ portfolio, orgSlug }: PortfolioTableProps) {
  const { t } = useTranslation();

  return (
    <Card data-testid="portfolio-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.portfolio.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {portfolio.projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("executive.portfolio.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">
                    {t("executive.portfolio.project")}
                  </th>
                  <th className="pb-2 font-medium">
                    {t("executive.portfolio.gates")}
                  </th>
                  <th className="pb-2 font-medium">
                    {t("executive.portfolio.lastActivity")}
                  </th>
                  <th className="pb-2 font-medium">
                    {t("executive.portfolio.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {portfolio.projects.map((p) => (
                  <tr key={p.slug} className="border-b last:border-0">
                    <td className="py-2">
                      <a
                        href={`/${orgSlug}/projects/${p.slug}`}
                        className="text-primary hover:underline"
                      >
                        {p.name}
                      </a>
                    </td>
                    <td className="py-2">
                      {p.gatePassRate !== null
                        ? `${p.gatePassRate.toFixed(0)}%`
                        : "—"}
                    </td>
                    <td className="py-2">
                      {p.lastActivityDate ?? t("executive.portfolio.never")}
                    </td>
                    <td className="py-2">
                      {p.needsAttention ? (
                        <Badge variant="destructive" className="text-xs">
                          {t("executive.portfolio.attention")}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          {t("executive.portfolio.healthy")}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
